import { zodResolver } from "@hookform/resolvers/zod";
import { Button } from "@hortis/ui/button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@hortis/ui/dialog";
import { FormControl, FormField, FormItem, FormMessage } from "@hortis/ui/form";
import { Input, InputSlot } from "@hortis/ui/input";
import { Tab, TabContent, TabHeader, TabList } from "@hortis/ui/tab";
import { type ReactNode, useEffect, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { stopEventPropagation } from "src/features/collection/components/stop-event-propagation";
import { twMerge } from "tailwind-merge";
import { match } from "ts-pattern";
import { z } from "zod";

const schema = z.object({
  weight: z.string().nullish(),
  quantity: z.string().nullish(),
  massPlanting: z.boolean().optional(),
});

export type QuantityWeightTab = "quantity" | "weight" | "massPlanting";

export const QuantityWeightDialog = ({
  open,
  onOpenChange,
  onSubmit,
  children,
  initialValue,
  initialTab = "quantity",
}: {
  initialValue: z.infer<typeof schema> | undefined;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (value: z.infer<typeof schema>) => void;
  children: ReactNode;
  initialTab?: QuantityWeightTab;
}) => {
  const [tab, setTab] = useState<QuantityWeightTab>(initialTab);
  const form = useForm<z.infer<typeof schema>>({
    defaultValues: initialValue ?? {
      weight: undefined,
      quantity: undefined,
      massPlanting: false,
    },
    resolver: zodResolver(schema),
  });

  useEffect(() => {
    if (!open) {
      return;
    }
    form.reset(initialValue);
  }, [open, initialValue, form]);

  useEffect(() => {
    if (!open) {
      return;
    }
    setTab(initialTab);
  }, [initialTab, open]);

  const handleSubmit = (data: z.infer<typeof schema>) => {
    if (tab === "massPlanting") {
      onSubmit({ massPlanting: true, quantity: undefined, weight: undefined });
    }
    if (tab === "quantity") {
      onSubmit({
        quantity: data.quantity === "" ? undefined : data.quantity,
        weight: undefined,
        massPlanting: false,
      });
    }
    if (tab === "weight") {
      onSubmit({
        weight: data.weight === "" ? undefined : data.weight,
        quantity: undefined,
        massPlanting: false,
      });
    }
    onOpenChange(false);
  };

  const label = match(tab)
    .with("quantity", () => "quantity")
    .with("weight", () => "weight")
    .with("massPlanting", () => "mass planting")
    .exhaustive();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger asChild onFocusCapture={stopEventPropagation}>
        {children}
      </DialogTrigger>
      <FormProvider {...form}>
        <DialogContent className="gap-5!" asChild>
          <form
            onSubmit={(e) => {
              e.stopPropagation();
              void form.handleSubmit(handleSubmit)(e);
            }}
          >
            <DialogHeader className="space-y-4">
              <DialogTitle className="flex items-center gap-2">
                Set {label}
              </DialogTitle>
              <Tab
                value={tab}
                onValueChange={setTab}
                className="w-full border-none shadow-none"
              >
                <TabList>
                  <TabHeader
                    value="quantity"
                    className={twMerge(
                      "rounded-b-none! rounded-tl-lg! border-l! border-t",
                      tab === "massPlanting" && "rounded-l-lg! border-y",
                    )}
                    data-cy="quantity-tab"
                  >
                    Quantity
                  </TabHeader>
                  <TabHeader
                    value="weight"
                    className={twMerge(
                      "rounded-none border-t",
                      tab === "massPlanting" && "border-y",
                    )}
                    data-cy="weight-tab"
                  >
                    Weight
                  </TabHeader>
                  <TabHeader
                    value="massPlanting"
                    className={twMerge(
                      "rounded-b-none! rounded-tr-lg! border-r border-t",
                      tab === "massPlanting" && "rounded-r-lg! border-y",
                    )}
                    data-cy="mass-planting-tab"
                  >
                    Mass planting
                  </TabHeader>
                </TabList>
                <TabContent value="quantity" asChild>
                  <FormField
                    control={form.control}
                    name="quantity"
                    render={({ field }) => (
                      <FormItem>
                        <FormControl>
                          <Input
                            value={field.value ?? undefined}
                            onChange={field.onChange}
                            placeholder="Enter quantity..."
                            className="rounded-tl-none"
                            data-cy="quantity-input"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </TabContent>
                <TabContent value="weight" asChild>
                  <FormField
                    control={form.control}
                    name="weight"
                    render={({ field }) => (
                      <FormItem>
                        <FormControl>
                          <Input
                            value={field.value ?? undefined}
                            onChange={field.onChange}
                            placeholder="Enter weight..."
                            className="rounded-tl-none"
                            data-cy="weight-input"
                          >
                            <InputSlot
                              side="right"
                              className="text-sm font-medium text-grey-500"
                            >
                              grams
                            </InputSlot>
                          </Input>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </TabContent>
              </Tab>
            </DialogHeader>
            <DialogFooter>
              <DialogClose asChild>
                <Button testId="cancel-quantity-weight-button">Cancel</Button>
              </DialogClose>
              <Button
                variant="primary"
                type="submit"
                testId="save-quantity-weight-button"
              >
                Set {label}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </FormProvider>
    </Dialog>
  );
};
